import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from '@repletoryreach/sdk-api/react';
import { AuthProvider } from '@repletoryreach/sdk-auth';
import { RealtimeProvider } from '@repletoryreach/sdk-realtime';
import { UIProvider } from '@repletoryreach/sdk-ui';
import { NotificationsProvider } from '@repletoryreach/sdk-ui/notifications';
import { ModalsProvider } from '@repletoryreach/sdk-ui/modals';
import { Spotlight } from '@repletoryreach/sdk-ui/spotlight';
import { NavigationProgress } from '@repletoryreach/sdk-ui/nprogress';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { palette } from '@repletoryreach/sdk-utils/color';

import { I18nProvider } from './i18n/I18nProvider';
import { FileViewerHost } from './components/FileViewerHost';
import { AppRoutes } from './app/routes';
import './styles.css';
import './styles/list-row.css';

// The console's primary is blue, matching the reference designs in
// `_UI_improvements/console/images/`: every primary button, link, focus ring
// and active nav row there is blue. Amber survives as the *admin* accent, on
// the brand badge and the "Admin console" pill, which is what distinguishes
// this console from the tenant one at a glance.
//
// Applied once at module load so UIProvider's first CSS-var computation already
// uses it, otherwise the store's teal default ships for the first paint. The
// value comes from the shared palette rather than a literal, so the console and
// the design system cannot drift apart, and it is a concrete colour because the
// store feeds it straight into `--rr-color-primary`.
const ADMIN_PRIMARY = palette.blue[600];
if (useAppStore.getState().primary !== ADMIN_PRIMARY) {
  useAppStore.getState().setPrimary(ADMIN_PRIMARY);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 }
  }
});

// Same-origin in dev via Vite proxy ('/api' → gateway, '/hubs' → gateway WS).
// Prod ingress mirrors the same path prefixes so this value is unchanged.
const apiBaseUrl = import.meta.env.VITE_API_BASE ?? '/api';
const realtimeBaseUrl = import.meta.env.VITE_REALTIME_BASE ?? '';

function AppDataTag({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    document.documentElement.setAttribute('data-rr-app', 'admin');
  }, []);
  return <>{children}</>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Opt in to the v7 behaviours now. Without these two flags React Router
        logs a future-flag warning on every page load, which buries the console
        errors worth reading. */}
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ConfigProvider config={{ apiBaseUrl, realtimeBaseUrl }}>
        <UIProvider defaultColorScheme="auto">
          <AppDataTag>
            <AuthProvider domain="platform">
              <RealtimeProvider>
                <QueryClientProvider client={queryClient}>
                  <I18nProvider>
                    <ModalsProvider>
                      <FileViewerHost>
                        <NotificationsProvider position="bottom-right" />
                        <NavigationProgress />
                        <Spotlight />
                        <AppRoutes />
                      </FileViewerHost>
                    </ModalsProvider>
                  </I18nProvider>
                </QueryClientProvider>
              </RealtimeProvider>
            </AuthProvider>
          </AppDataTag>
        </UIProvider>
      </ConfigProvider>
    </BrowserRouter>
  </StrictMode>
);
